export const who = {

  mounted(el, binding) { binding.value(el) }
}

export const scrollto = {
  mounted(el, { modifiers, value }) {
    const isScrollable = (n) => {
      if (!(n instanceof HTMLElement)) return false;
      const s = getComputedStyle(n);
      const canY = (s.overflowY === 'auto' || s.overflowY === 'scroll') && n.scrollHeight > n.clientHeight;
      const canX = (s.overflowX === 'auto' || s.overflowX === 'scroll') && n.scrollWidth > n.clientWidth;
      return canX || canY;
    };

    const findScrollable = (n) => {
      for (let p = n.parentElement; p; p = p.parentElement) if (isScrollable(p)) return p;
      return null;
    };

    const resolveContainer = () => {
      if (value) {
        if (typeof value === 'string') {
          // 1º intenta con closest (por si es ancestro), si no, en el documento
          return el.closest(value) || document.querySelector(value);
        }
        if (value instanceof Element) return value;
      }
      return findScrollable(el);
    };

    const onClick = (e) => {
      if (!el.contains(e.target)) return;
      const c = resolveContainer();
      if (!c) return;

      const maxX = c.scrollWidth - c.clientWidth;
      const maxY = c.scrollHeight - c.clientHeight;
      const behavior = 'smooth';

      if (modifiers.left) c.scrollTo({ left: 0, top: c.scrollTop, behavior });
      else if (modifiers.right) c.scrollTo({ left: Math.max(0, maxX), top: c.scrollTop, behavior });
      else if (modifiers.top) c.scrollTo({ top: 0, left: c.scrollLeft, behavior });
      else if (modifiers.bottom) c.scrollTo({ top: Math.max(0, maxY), left: c.scrollLeft, behavior });
      else c.scrollTo({ left: 0, top: c.scrollTop, behavior }); // por defecto: izquierda
    };

    el.__scrollOnClick__ = onClick;
    el.addEventListener('click', onClick);
  },
  beforeUnmount(el) {
    el.removeEventListener('click', el.__scrollOnClick__);
    delete el.__scrollOnClick__;
  }
}

export const hscroll = {

  mounted(el, binding) {
    const o = binding?.value || {};
    const factor = o.factor ?? 1.2;
    const invert = !!o.invert;
    const friction = Math.min(Math.max(o.friction ?? 0.1, 0), 1);
    const maxVelocity = o.maxVelocity ?? 120;

    el.style.overflowX ||= 'auto';
    el.style.overscrollBehaviorX ||= 'contain';
    el.style.scrollBehavior = 'auto';

    let v = 0, raf = 0, last = 0;

    const normalizeDelta = (e) => {
      const unit = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? el.clientWidth * 0.9 : 1);
      const d = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      return (invert ? -1 : 1) * d * unit * factor;
    };

    const step = (ts) => {
      if (!last) last = ts;
      const dt = Math.min(50, ts - last); last = ts;

      const capped = Math.max(-maxVelocity, Math.min(maxVelocity, v));
      el.scrollLeft += capped;

      v *= Math.pow(1 - friction, dt / 16);

      const atEdge = (el.scrollLeft <= 0 && v < 0) ||
        (el.scrollLeft >= el.scrollWidth - el.clientWidth && v > 0);
      if (Math.abs(v) < 0.1 || atEdge) { v = 0; raf = 0; return; }
      raf = requestAnimationFrame(step);
    };

    const onWheel = (e) => {
      e.preventDefault();
      v += normalizeDelta(e) / 12;
      if (Math.abs(v) > maxVelocity) v = Math.sign(v) * maxVelocity;
      if (!raf) { last = 0; raf = requestAnimationFrame(step); }
    };

    el.__hscroll__ = { onWheel };
    el.addEventListener('wheel', onWheel, { passive: false });
  },
  unmounted(el) {
    const r = el.__hscroll__;
    if (r) el.removeEventListener('wheel', r.onWheel, { passive: false });
    delete el.__hscroll__;
  }
}

export const scrollPercent = {

  mounted(el, binding) {
    // Soporta: v-scroll-percent="fn"  o  v-scroll-percent="{ update: fn, percentStep: 0.1 }"
    const v = binding?.value;
    const update =
      typeof v === 'function' ? v :
        (v && typeof v.update === 'function' ? v.update : null);
    if (!update) return;

    const stepIn =
      typeof v === 'object' && typeof v.percentStep === 'number' ? v.percentStep : undefined;
    const step = Math.min(Math.max(stepIn ?? 0.1, 0.001), 1);

    const decimals = (() => {
      const s = String(step); const i = s.indexOf('.');
      return i === -1 ? 0 : (s.length - i - 1);
    })();

    const quantize = () => {
      const max = Math.max(1, el.scrollWidth - el.clientWidth);
      const raw = (el.scrollLeft / max) * 100; // 0..100
      const bucket = raw <= 0 ? 0 :
        raw >= 100 ? Math.round(100 / step) :
          Math.floor(raw / step);
      const snapped = Math.min(100, Math.max(0, bucket * step));
      update(Number(snapped.toFixed(decimals)));
    };

    const onScroll = () => quantize();

    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(quantize); // reevalúa al cambiar el tamaño del contenedor
    ro.observe(el);

    el.__scrollPercentCb__ = { onScroll, ro };
    quantize(); // inicial
  },

  unmounted(el) {
    const r = el.__scrollPercentCb__;
    if (!r) return;
    el.removeEventListener('scroll', r.onScroll, { passive: true });
    r.ro?.disconnect();
    delete el.__scrollPercentCb__;
  }
}

export const helpTooltip = {
  mounted(el, binding) {
    if (!binding?.value) return;

    const opts = {
      placement: binding.value.placement || "mid-end", // "top|mid|bottom"-"start|center|end"
      outside: !!binding.value.outside,
      offsetX: Number.isFinite(binding.value.offsetX) ? binding.value.offsetX : 0, // px
      offsetY: Number.isFinite(binding.value.offsetY) ? binding.value.offsetY : 0, // px
      onClick: binding.value.onClick || (() => { }),
      hideDelay: Number.isFinite(binding.value.hideDelay) ? binding.value.hideDelay : 100,
    };

    // --- Círculo en <body> (fixed), sin tamaño forzado ---
    const circle = document.createElement("button");
    circle.type = "button";
    circle.setAttribute("aria-label", "Ayuda");
    circle.setAttribute("tabindex", "0");
    circle.className = [
      "fixed",
      "rounded-full",
      "inline-flex", "items-center", "justify-center",
      "cursor-pointer", "select-none", "outline-none",
      "z-50",
      "m-0", "p-0", "box-content",
      "opacity-0", "transition-opacity", "duration-150", "before:inset-1 before:absolute before:rounded-full before:bg-white",
      "pointer-events-none",
      "button-hover",
    ].join(" ");
    circle.style.margin = "0";
    circle.style.padding = "0";
    circle.innerHTML = `<span class="font-bold leading-none bg-pro text-bg-clip ri-information-2-fill text-2xl z-5"></span>`;
    document.body.appendChild(circle);

    let rafId = 0;
    let ro = null;

    const parsePlacement = () => {
      const [row, col] = String(opts.placement).split("-");
      return { row: row || "mid", col: col || (row === "mid" ? "end" : "center") };
    };

    const place = () => {
      // medidas frescas
      const r = el.getBoundingClientRect();
      const cr = circle.getBoundingClientRect();
      let w = cr.width, h = cr.height;

      if (w < 1 || h < 1) {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const cr2 = circle.getBoundingClientRect();
          w = cr2.width; h = cr2.height;
          if (w < 1 || h < 1) {
            if (!ro) {
              ro = new ResizeObserver(() => {
                const cr3 = circle.getBoundingClientRect();
                if (cr3.width > 0 && cr3.height > 0) place();
              });
              ro.observe(circle);
            }
            return;
          }
          place();
        });
        return;
      }

      const { row, col } = parsePlacement();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;

      // 1) Posición base (inside/outside), SIN offsets
      let left, top;
      if (row === "top") {
        top = opts.outside ? (r.top - h) : r.top;
        if (col === "start") left = r.left;
        else if (col === "center") left = cx - w / 2;
        else left = opts.outside ? r.right : (r.right - w); // end
      } else if (row === "bottom") {
        top = opts.outside ? r.bottom : (r.bottom - h);
        if (col === "start") left = r.left;
        else if (col === "center") left = cx - w / 2;
        else left = opts.outside ? r.right : (r.right - w);
      } else { // mid
        if (col === "start") left = opts.outside ? (r.left - w) : r.left;
        else if (col === "center") left = cx - w / 2;
        else left = opts.outside ? r.right : (r.right - w);
        top = cy - h / 2;
      }

      // 2) Offsets con signo (según posición, NO según outside)
      if (row === "top") {
        top += -opts.offsetY;                     // +Y arriba
        if (col === "start") left += -opts.offsetX;   // +X izquierda
        else if (col === "end") left += +opts.offsetX; // +X derecha
        // center: no tocar X salvo que quieras lateral explícito, entonces usa offsetX directamente:
        if (col === "center") left += opts.offsetX;
      } else if (row === "bottom") {
        top += +opts.offsetY;                     // +Y abajo
        if (col === "start") left += -opts.offsetX;
        else if (col === "end") left += +opts.offsetX;
        if (col === "center") left += opts.offsetX;
      } else {
        // mid: offsetX = perpendicular (izq/der), offsetY = lateral (arriba/abajo)
        if (col === "start") left += -opts.offsetX;
        else if (col === "end") left += +opts.offsetX;
        else left += 0; // center
        top += opts.offsetY; // + abajo, - arriba
      }

      circle.style.left = `${left}px`;
      circle.style.top = `${top}px`;
    };

    // visibilidad + ocultado instantáneo en scroll/wheel
    let overHost = false, overCircle = false, hideT = null;

    const show = () => {
      clearTimeout(hideT);
      place();
      circle.classList.remove("opacity-0", "pointer-events-none");
      circle.classList.add("opacity-100", "pointer-events-auto");
    };

    const hide = (immediate = false) => {
      clearTimeout(hideT);
      if (immediate) {
        circle.classList.remove("opacity-100", "pointer-events-auto");
        circle.classList.add("opacity-0", "pointer-events-none");
        return;
      }
      hideT = setTimeout(() => {
        if (!overHost && !overCircle) {
          circle.classList.remove("opacity-100", "pointer-events-auto");
          circle.classList.add("opacity-0", "pointer-events-none");
        }
      }, opts.hideDelay);
    };

    const onHostEnter = () => { overHost = true; show(); };
    const onHostLeave = (ev) => {
      const to = ev.relatedTarget;
      if (to && (to === circle || circle.contains(to))) { overHost = false; return; }
      overHost = false; hide();
    };
    const onCircleEnter = () => { overCircle = true; show(); };
    const onCircleLeave = (ev) => {
      const to = ev.relatedTarget;
      if (to && (to === el || el.contains(to))) { overCircle = false; return; }
      overCircle = false; hide();
    };

    el.addEventListener("pointerenter", onHostEnter);
    el.addEventListener("pointerleave", onHostLeave);
    circle.addEventListener("pointerenter", onCircleEnter);
    circle.addEventListener("pointerleave", onCircleLeave);

    circle.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); circle.click(); }
    });
    circle.addEventListener("click", (ev) => { opts.onClick(el, ev); ev.stopPropagation(); });

    const onAnyScroll = () => hide(true);
    window.addEventListener("scroll", onAnyScroll, { passive: true, capture: true });
    window.addEventListener("wheel", onAnyScroll, { passive: true, capture: true });

    const onResize = () => { if (circle.classList.contains("pointer-events-auto")) place(); };
    window.addEventListener("resize", onResize, { passive: true });

    el.__vHelpCircle = {
      circle, opts,
      handlers: { onHostEnter, onHostLeave, onCircleEnter, onCircleLeave, onAnyScroll, onResize },
    };
  },

  updated(el, binding) {
    const s = el.__vHelpCircle;
    if (!s) return;

    s.opts = {
      placement: binding.value.placement ?? s.opts.placement,
      outside: !!binding.value.outside,
      offsetX: Number.isFinite(binding.value.offsetX) ? binding.value.offsetX : s.opts.offsetX,
      offsetY: Number.isFinite(binding.value.offsetY) ? binding.value.offsetY : s.opts.offsetY,
      onClick: binding.value.onClick || s.opts.onClick,
      hideDelay: Number.isFinite(binding.value.hideDelay) ? binding.value.hideDelay : s.opts.hideDelay,
    };

    if (s.circle.classList.contains("pointer-events-auto")) {
      s.circle.classList.add("opacity-0", "pointer-events-none");
      s.circle.classList.remove("opacity-100", "pointer-events-auto");
      requestAnimationFrame(() => {
        const ev = new Event("pointerenter");
        el.dispatchEvent(ev);
      });
    }
  },

  beforeUnmount(el) {
    const s = el.__vHelpCircle;
    if (!s) return;

    el.removeEventListener("pointerenter", s.handlers.onHostEnter);
    el.removeEventListener("pointerleave", s.handlers.onHostLeave);
    s.circle.removeEventListener("pointerenter", s.handlers.onCircleEnter);
    s.circle.removeEventListener("pointerleave", s.handlers.onCircleLeave);

    window.removeEventListener("scroll", s.handlers.onAnyScroll, true);
    window.removeEventListener("wheel", s.handlers.onAnyScroll, true);
    window.removeEventListener("resize", s.handlers.onResize);

    s.circle.remove();
    delete el.__vHelpCircle;
  },
}